import Track from '../models/track/track';
import { invalidateTrackSearch } from './trackSearch';

/**
 * Editing a track's metadata by hand, without touching the audio file.
 *
 * The library record is the metadata layer (docs/TAG-EDITING.md, tier one).
 * A hand edit writes the record and marks the field in `track.userEdited`,
 * and every tag read from then on leaves that field alone. The mark is the
 * only thing that separates "the tag said so" from "the user said so", so
 * both writers - here and `applyTagsToTrack` - honour it.
 *
 * Everything in this module is plain data manipulation on the JSON shape
 * tracks actually have at runtime. No class instance methods, nothing that
 * would not survive a JSON round trip (ARCHITECTURE.md section 2).
 */

/** Every field the editor can set. The five the constructor takes, then the tag extras. */
export const editableTrackFields = [
  'title',
  'artist',
  'album',
  'albumArtist',
  'genre',
  'releaseDate',
  'trackNumber',
  'disc',
  'composer',
  'comment',
  'bpm',
  'initialKey',
  'publisher',
] as const;

export type EditableTrackField = (typeof editableTrackFields)[number];

/**
 * One edit, applied to one or many tracks. A field that is absent or
 * `undefined` is left alone, so a batch edit can set album on ten tracks
 * without touching their titles. `null` clears the field: it goes back to
 * the constructor default for the five constructor fields and disappears
 * from the record for the optional ones, and its user-edited mark is dropped
 * with it, so the next tag read may fill it again.
 */
export type TrackEdits = {
  [K in EditableTrackField]?: Track[K] | null;
};

/** Marker for a field whose value differs across a selection. */
export const MIXED: unique symbol = Symbol('mixed');

export type MergedTrackFields = {
  [K in EditableTrackField]: Track[K] | typeof MIXED;
};

const numericFields: ReadonlySet<EditableTrackField> = new Set<EditableTrackField>([
  'releaseDate',
  'trackNumber',
  'disc',
  'bpm',
]);

const constructorDefaults: Readonly<
  Partial<Record<EditableTrackField, Track[EditableTrackField]>>
> = {
  title: 'Title Missing',
  artist: '',
  album: '',
  genre: '',
  releaseDate: 0,
};

export function isNumericTrackField(field: EditableTrackField) {
  return numericFields.has(field);
}

export function isUserEdited(track: Track, field: EditableTrackField) {
  return track.userEdited !== undefined && track.userEdited[field] === true;
}

export function hasUserEdits(track: Track) {
  return track.userEdited !== undefined && Object.keys(track.userEdited).length > 0;
}

/**
 * The value the editor shows and compares. `releaseDate` is a timestamp in
 * the record but a year to the user; nobody edits a day.
 */
export function releaseDateToYear(releaseDate: number | undefined) {
  if (!releaseDate) return undefined;

  const year = new Date(releaseDate).getFullYear();

  return isNaN(year) ? undefined : year;
}

/** Matches the tag-read code: a year lands on 1 January, local time. */
export function yearToReleaseDate(year: number) {
  return new Date(year, 0).valueOf();
}

/**
 * Prefill for the editor. A field every track agrees on shows its value;
 * one they disagree on shows MIXED and is left untouched unless typed into.
 * Empty and absent count as the same value, so a blank genre on all ten is
 * "blank", not "mixed". Dates compare by year, since that is what is shown.
 */
export function mergeTrackFields(tracks: Track[]): MergedTrackFields {
  const merged = {} as MergedTrackFields;

  for (const field of editableTrackFields) {
    let value: any;
    let first = true;

    for (const track of tracks) {
      let current: any = track[field];

      if (field === 'releaseDate') current = releaseDateToYear(current);
      if (current === '' || current === 0) current = undefined;

      if (first) {
        value = current;
        first = false;
      } else if (current !== value) {
        value = MIXED;
        break;
      }
    }

    merged[field] = value;
  }

  return merged;
}

/**
 * Writes `edits` onto every track and marks the written fields. Returns the
 * tracks that actually changed, so the caller can skip a library write when
 * the user pressed Save with nothing new.
 */
export function applyTrackEdits(tracks: Track[], edits: TrackEdits): Track[] {
  const changed: Track[] = [];

  for (const track of tracks) {
    let touched = false;

    for (const field of editableTrackFields) {
      if (!(field in edits) || edits[field] === undefined) continue;

      const value = edits[field];

      // Title is the one field nothing may blank: every row renders it and
      // "Title Missing" is worse than whatever was there.
      if (field === 'title' && (value === null || String(value).trim() === '')) continue;

      if (value === null) {
        touched = clearField(track, field) || touched;
        continue;
      }

      const next: any = typeof value === 'string' ? value.trim() : value;

      if (track[field] !== next) {
        (track as any)[field] = next;
        touched = true;
      }

      if (!isUserEdited(track, field)) {
        track.userEdited = { ...track.userEdited, [field]: true };
        touched = true;
      }
    }

    if (touched) {
      invalidateTrackSearch(track);
      changed.push(track);
    }
  }

  return changed;
}

/**
 * Drops every user-edited mark. The values stay as they are; the next tag
 * read is free to overwrite them. That is the undo for a hand edit.
 */
export function clearUserEdits(tracks: Track[]): Track[] {
  const changed: Track[] = [];

  for (const track of tracks) {
    if (hasUserEdits(track)) {
      delete track.userEdited;
      changed.push(track);
    }
  }

  return changed;
}

function clearField(track: Track, field: EditableTrackField) {
  let touched = false;

  if (field in constructorDefaults) {
    if (track[field] !== constructorDefaults[field]) {
      (track as any)[field] = constructorDefaults[field];
      touched = true;
    }
  } else if (track[field] !== undefined) {
    delete track[field];
    touched = true;
  }

  if (isUserEdited(track, field)) {
    const { [field]: _dropped, ...rest } = track.userEdited!;

    if (Object.keys(rest).length > 0) {
      track.userEdited = rest;
    } else {
      delete track.userEdited;
    }
    touched = true;
  }

  return touched;
}
